import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, TextField } from "@mui/material";
import { commonBoxStyle, MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../framework/redux/hooks";
import { handleApiError } from "../../services/baseService";
import { saveWorkShiftComment } from "../../framework/redux/slices/workShiftTimeSlotSlice";
import { useSelectCurrentWorkShiftTimeSlot } from "../../hooks/worktime/useSelectWorkShiftTimeSlots";
import { addSuccessMsg } from "../../framework/redux/slices/appSlice";
import { FormikProps, useFormik } from "formik";
import * as yup from "yup";
import { defaultFormikConfig, formikFieldProps } from "../framework/formUtils";
import { IWorkTimeCommentSaveData } from "../../models/workShiftTimeSlot/workTimeCommentSaveData";
import { SubmitButton } from "../framework/formButtons";
import { LoadingIndicator } from "../framework/loadingIndicator";

export const WorkTimeEditCommentForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const current = useSelectCurrentWorkShiftTimeSlot();

    const handleSubmit = async (formData: IWorkTimeCommentSaveData) => {
        await dispatch(saveWorkShiftComment(formData))
            .unwrap()
            .then(() => {
                dispatch(addSuccessMsg(t("workTime.workTimeSaveSuccessful")));
                goBack();
            })
            .catch(handleApiError);
    };

    const goBack = () => {
        navigate("/");
    };

    const formik: FormikProps<IWorkTimeCommentSaveData> = useFormik({
        ...defaultFormikConfig,
        initialValues: current,
        onSubmit: handleSubmit,
        validationSchema: yup.object({
            comment: yup.string()
                .required(
                    t("validation.fieldIsRequired", {
                        field: t("common.comment"),
                    })
                )
                .matches(/^(?!\s+$).*/, t("validation.onlyWhitespacesNotAllowed", {
                    field: t("common.comment"),
                }))
        }),
    });

    return (
        <MainLayout
            hasPx
            topComponent={
                <PageTitle
                    header={`${t("workTime.workShiftComment")}`}
                />
            }
        >
            {current?.id ? (
                <>
                    <Box sx={commonBoxStyle} mb={2}>
                        <TextField
                            name="comment"
                            label={t("common.comment")}
                            multiline
                            minRows={2}
                            fullWidth
                            {...formikFieldProps(formik, "comment")}
                        />
                    </Box>
                    <Box sx={commonBoxStyle}>
                        <Button
                            onClick={() => void goBack()}
                            variant="outlined"
                            color="success"
                        >
                            {t("common.cancel")}
                        </Button>
                        <SubmitButton formik={formik} />
                    </Box>
                </>
            ) : (
                <LoadingIndicator />
            )}
        </MainLayout>
    );
};
